import type { EnrichedMessage, Reaction } from "@/frontend/lib/types";
import { cn } from "@/lib/utils";
import { Play, Pause, Download, Reply } from "lucide-react";
import { useState, useRef, useEffect } from "react";

interface MessageBubbleProps {
  message: EnrichedMessage;
  messages: EnrichedMessage[];
  onReplyClick?: (messageId: number) => void;
}

export function MessageBubble({ message, messages, onReplyClick }: MessageBubbleProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const audioRef = useRef<HTMLAudioElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  const formatTime = (time: number): string => {
    if (isNaN(time)) return "0:00";
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
  };

  const handlePlayPause = () => {
    if (!audioRef.current) return;

    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration);
    }
  };

  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    const time = parseFloat(e.target.value);
    if (audioRef.current) {
      audioRef.current.currentTime = time;
      setCurrentTime(time);
    }
  };

  const handleEnded = () => {
    setIsPlaying(false);
    setCurrentTime(0);
  };

  const getFirstLine = (content: string): string => {
    const text = content.replace(/<br\s*\/?>/gi, '\n').replace(/<[^>]*>/g, '');
    const firstLine = text.split('\n')[0]?.trim() || '';
    return firstLine.length > 50 ? firstLine.substring(0, 50) + '...' : firstLine;
  };

  const groupedReactions = (reactions: Reaction[]) => {
    const groups = new Map<string, { emoji: string; count: number; users: string[] }>();

    reactions.forEach((reaction) => {
      const existing = groups.get(reaction.emoji);
      const userName = reaction.from_name || `User ${reaction.from_id}`;
      const truncatedName = userName.length > 25 ? userName.substring(0, 25) + '...' : userName;

      if (existing) {
        existing.count++;
        existing.users.push(truncatedName);
      } else {
        groups.set(reaction.emoji, {
          emoji: reaction.emoji,
          count: 1,
          users: [truncatedName],
        });
      }
    });

    return Array.from(groups.values());
  };

  if (message.type === "service") {
    return (
      <div className="flex justify-center py-2">
        <p className="text-xs text-muted-foreground">{message.content}</p>
      </div>
    );
  }

  const isEmployee = message.fromType === "employee";
  const isClient = message.fromType === "client";

  const repliedMessage = message.replyTo
    ? messages.find(m => m.telegramMessageId === message.replyTo)
    : null;

  const currentIndex = messages.findIndex(m => m.id === message.id);
  const previousMessage = currentIndex > 0 ? messages[currentIndex - 1] : null;

  const isFirstInGroup = !previousMessage ||
    previousMessage.fromTelegramId !== message.fromTelegramId ||
    previousMessage.type === "service" ||
    message.showDateSeparator;

  const hasMedia = message.voiceUrl || message.photoUrl || message.videoUrl || message.videoNoteUrl || message.fileUrl;

  return (
    <div id={`message-${message.id}`} className={cn("flex w-full gap-2", isFirstInGroup ? "pt-2 pb-0.5" : "py-0.5", isClient && "justify-end")}>
      <div className={cn("flex max-w-[70%] flex-col gap-1", isClient && "items-end")}>
        {isFirstInGroup && isEmployee && message.employee && (
          <div className="flex items-center gap-2 px-3">
            <span className="text-sm font-medium text-foreground">{message.employee.name}</span>
            <span className="text-xs text-muted-foreground">{message.employee.role}</span>
          </div>
        )}

        {isFirstInGroup && isClient && message.fromName && (
          <div className="flex items-center gap-2 px-3">
            <span className="text-sm font-medium text-foreground">{message.fromName}</span>
            <span className="text-xs text-muted-foreground">Client</span>
          </div>
        )}

        <div
          className={cn(
            "rounded-lg bg-secondary text-secondary-foreground overflow-hidden",
          )}
        >
          {repliedMessage && (
            <button
              onClick={() => onReplyClick?.(message.replyTo!)}
              className={cn(
                "flex items-center gap-2 w-full bg-muted px-3 py-1.5 text-left transition-colors hover:bg-accent",
              )}
            >
              <Reply className="h-3 w-3 flex-shrink-0 text-muted-foreground" />
              <span className="text-xs text-muted-foreground truncate">
                {repliedMessage.photoUrl
                  ? 'Photo'
                  : repliedMessage.videoUrl || repliedMessage.videoNoteUrl
                  ? 'Video'
                  : repliedMessage.voiceUrl
                  ? 'Voice message'
                  : repliedMessage.fileUrl
                  ? 'File'
                  : repliedMessage.content
                  ? getFirstLine(repliedMessage.content)
                  : 'Message'}
              </span>
            </button>
          )}

          <div className="px-4 pt-2 pb-2 relative">
            {message.voiceUrl && (
              <div className="mb-2">
                <div className="flex items-center gap-3 w-full min-w-[250px]">
                  <button
                    onClick={handlePlayPause}
                    className="flex h-9 w-9 items-center justify-center rounded-full bg-primary text-primary-foreground transition-colors hover:bg-primary/90 flex-shrink-0"
                  >
                    {isPlaying ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4 ml-0.5" />}
                  </button>

                  <div className="flex-1 flex flex-col gap-1">
                    <input
                      type="range"
                      min="0"
                      max={duration || 0}
                      value={currentTime}
                      onChange={handleSeek}
                      className="w-full h-1 bg-muted rounded-lg appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-3 [&::-webkit-slider-thumb]:h-3 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-primary [&::-moz-range-thumb]:w-3 [&::-moz-range-thumb]:h-3 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-primary [&::-moz-range-thumb]:border-0"
                    />
                    <div className="flex justify-between text-[10px] text-muted-foreground">
                      <span>{formatTime(currentTime)}</span>
                      <span>{formatTime(duration)}</span>
                    </div>
                  </div>

                  <audio
                    ref={audioRef}
                    src={message.voiceUrl}
                    onTimeUpdate={handleTimeUpdate}
                    onLoadedMetadata={handleLoadedMetadata}
                    onEnded={handleEnded}
                  />
                </div>
              </div>
            )}

            {message.photoUrl && (
              <div className="mb-2">
                <button onClick={() => setIsFullscreen(true)} className="overflow-hidden rounded-lg">
                  <img
                    src={message.photoUrl}
                    alt="Photo"
                    className="max-h-64 w-auto transition-transform hover:scale-105"
                  />
                </button>
              </div>
            )}

            {(message.videoUrl || message.videoNoteUrl) && (
              <div className="mb-2">
                <button
                  onClick={() => setIsModalOpen(true)}
                  className="flex items-center gap-2 rounded-lg bg-background/20 px-3 py-2 transition-colors hover:bg-background/30"
                >
                  <Play className="h-5 w-5" />
                  <span className="text-sm">{message.videoNoteUrl ? "Video note" : "Video"}</span>
                </button>
              </div>
            )}

            {message.fileUrl && (
              <div className="mb-2">
                <a
                  href={message.fileUrl}
                  download
                  className="flex items-center gap-2 text-sm transition-opacity hover:opacity-80"
                >
                  <Download className="h-4 w-4" />
                  <span>Download file</span>
                </a>
              </div>
            )}

            {message.content && (
              <div
                className={cn(
                  "text-sm leading-relaxed break-words overflow-wrap-anywhere [&_a]:text-blue-500 [&_a]:underline [&_a]:break-all pb-4 whitespace-pre-wrap"
                )}
                dangerouslySetInnerHTML={{ __html: message.content }}
              />
            )}

            <span className="absolute bottom-1 right-2 text-[10px] text-muted-foreground/70">
              {new Date(message.createdAt).toLocaleTimeString("en-GB", {
                hour: "2-digit",
                minute: "2-digit",
                hour12: false,
              })}
            </span>
          </div>
        </div>

        {message.reactions && message.reactions.length > 0 && (
          <div className="flex flex-wrap gap-1 px-1 mt-1">
            {groupedReactions(message.reactions).map((group, idx) => (
              <div
                key={idx}
                className="group relative inline-flex items-center gap-1 rounded-full bg-secondary/50 px-2 py-0.5 text-xs transition-colors hover:bg-secondary"
              >
                <span>{group.emoji}</span>
                {group.count > 1 && <span className="text-muted-foreground">{group.count}</span>}

                <div className={cn(
                  "absolute bottom-full mb-2 hidden group-hover:block z-10 pointer-events-none",
                  isClient ? "right-0" : "left-0"
                )}>
                  <div className="bg-popover text-popover-foreground rounded-md px-3 py-2 text-xs shadow-md whitespace-nowrap border max-w-[200px] break-words">
                    {group.users.join(", ")}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {isModalOpen && (message.videoUrl || message.videoNoteUrl) && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80"
          onClick={() => setIsModalOpen(false)}
        >
          <div className="relative max-h-[90vh] max-w-[90vw]">
            <video
              src={message.videoUrl || message.videoNoteUrl || ""}
              controls
              autoPlay
              className="max-h-[90vh] max-w-[90vw]"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
      )}

      {isFullscreen && message.photoUrl && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/90"
          onClick={() => setIsFullscreen(false)}
        >
          <img
            src={message.photoUrl}
            alt="Fullscreen"
            className="max-h-[95vh] max-w-[95vw] object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
}
